import axios from 'axios'

import { promiseTimeout } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/commonUtils'
import {
  CardSide,
  HealthPlanCardUploadErrorCode,
  HealthPlanCardUploadResponse,
  OcrPollResponse,
  OcrStatus,
} from '@lyrahealth-inc/shared-app-logic/src/features/healthPlan/types/healthPlanCardUpload'
import {
  HEALTH_PLAN_OCR_MAX_POLL_ATTEMPTS,
  HEALTH_PLAN_OCR_POLL_INTERVAL_MS,
  isHealthPlanCardProcessingStatus,
  isHealthPlanCardTerminalStatus,
  validateUploadResponseStatus,
} from '@lyrahealth-inc/shared-app-logic/src/features/healthPlan/utils/healthPlanCardUploadUtils'
import { createCSVDownload } from '@lyrahealth-inc/shared-app-logic/src/features/profile/utils'
import {
  PostHealthPlanPaymentInfoBody,
  PostInfractionPaymentInfoBody,
} from '@lyrahealth-inc/shared-app-logic/src/models/healthPlan/HealthPlan'

import * as BannerActions from '../../../common/components/banner/banner-actions'
import {
  downloadPaymentsData,
  getEligibilityData,
  getHealthPlanPaymentData,
  getHealthPlanPaymentDataV2,
  getPaymentHistoryData,
  pollHealthPlanCardOcrData,
  removeHealthPlanInfoData,
  removeHealthPlanPaymentData,
  submitHealthPlanInfoData,
  submitHealthPlanPaymentData,
  submitSelfPayPaymentData,
  uploadHealthPlanCardData,
} from '../../../common/http/data/healthPlan'
import { getBaseApplicationData } from '../../../data/login/loginActions'
import { AppDispatch } from '../../../data/storeConfiguration/store'

export const SET_HEALTHPLAN_DATA = 'SET_HEALTHPLAN_DATA'
export const SET_PATIENT_DATA = 'SET_PATIENT_DATA'
export const SUBMIT_HEALTHPLAN_INFO = 'SUBMIT_HEALTHPLAN_INFO'
export const SUBMIT_SECONDARY_HEALTHPLAN_INFO = 'SUBMIT_SECONDARY_HEALTHPLAN_INFO'
export const REMOVE_HEALTHPLAN_INFO = 'REMOVE_HEALTHPLAN_INFO'
export const SUBMIT_HEALTHPLAN_PAYMENT = 'SUBMIT_HEALTHPLAN_PAYMENT'
export const UPDATE_HEALTHPLAN_PAYMENT = 'UPDATE_HEALTHPLAN_PAYMENT'
export const REMOVE_HEALTHPLAN_PAYMENT = 'REMOVE_HEALTHPLAN_PAYMENT'
export const UPDATE_INFRACTION_PAYMENT = 'UPDATE_INFRACTION_PAYMENT'
export const REMOVE_INFRACTION_PAYMENT = 'REMOVE_INFRACTION_PAYMENT'
export const START_HEALTHPLAN_AND_INFRACTION_CARD_SETUP = 'START_HEALTHPLAN_AND_INFRACTION_CARD_SETUP'
export const STOP_HEALTHPLAN_AND_INFRACTION_CARD_SETUP = 'STOP_HEALTHPLAN_AND_INFRACTION_CARD_SETUP'
export const UPDATE_HEALTHPLAN_AND_INFRACTION_CARD_REQUEST_TYPE = 'UPDATE_HEALTHPLAN_AND_INFRACTION_CARD_REQUEST_TYPE'
export const GET_PAYMENT_HISTORY = 'GET_PAYMENT_HISTORY'
export const GET_ELIGIBILITY_OPTIONS = 'GET_ELIGIBILITY_OPTIONS'
export const GET_HEALTHPLAN_PAYMENT = 'GET_HEALTHPLAN_PAYMENT'
export const GET_UNRESOLVED_INFRACTIONS = 'GET_UNRESOLVED_INFRACTIONS'

// Health Plan Card Upload OCR actions
export const UPLOAD_HEALTH_PLAN_CARD_PENDING = 'UPLOAD_HEALTH_PLAN_CARD_PENDING'
export const UPLOAD_HEALTH_PLAN_CARD_FULFILLED = 'UPLOAD_HEALTH_PLAN_CARD_FULFILLED'
export const UPLOAD_HEALTH_PLAN_CARD_REJECTED = 'UPLOAD_HEALTH_PLAN_CARD_REJECTED'
export const POLL_HEALTH_PLAN_CARD_OCR_PENDING = 'POLL_HEALTH_PLAN_CARD_OCR_PENDING'
export const POLL_HEALTH_PLAN_CARD_OCR_FULFILLED = 'POLL_HEALTH_PLAN_CARD_OCR_FULFILLED'
export const POLL_HEALTH_PLAN_CARD_OCR_REJECTED = 'POLL_HEALTH_PLAN_CARD_OCR_REJECTED'
export const RESET_CARD_UPLOAD_STATE = 'RESET_CARD_UPLOAD_STATE'

export const submitHealthPlanInfo = (lyraId: string, data: $TSFixMe) => {
  return (dispatch: AppDispatch) => {
    return new Promise(function (resolve, reject) {
      submitHealthPlanInfoData(lyraId, data).then(
        (response) => {
          if (response.data.medicalPlanEligibilityStatus !== 'ineligible') {
            dispatch({
              type: data?.isSecondary ? SUBMIT_SECONDARY_HEALTHPLAN_INFO : SUBMIT_HEALTHPLAN_INFO,
              payload: response,
            })
          }
          resolve(response)
        },
        (error) => {
          showErrorBanner(error, dispatch)
          reject(error)
        },
      )
    })
  }
}

export const removeHealthPlanInfo = (lyraId: string, isSecondary = false) => {
  return (dispatch: AppDispatch) => {
    return new Promise(function (resolve, reject) {
      removeHealthPlanInfoData(lyraId, isSecondary).then(
        (response) => {
          dispatch(getBaseApplicationData()) // refresh data flags
          dispatch(fetchPatientData())
          dispatch({
            type: REMOVE_HEALTHPLAN_INFO,
          })
          resolve(response)
        },
        (error) => {
          showErrorBanner(error, dispatch)
          reject(error)
        },
      )
    })
  }
}

export const submitHealthPlanPayment = (lyraId: string, data: PostHealthPlanPaymentInfoBody) => {
  return (dispatch: AppDispatch) => {
    return new Promise(function (resolve, reject) {
      submitHealthPlanPaymentData(lyraId, data).then(
        (response) => {
          dispatch({
            type: SUBMIT_HEALTHPLAN_PAYMENT,
            payload: response,
          })
          resolve(response)
        },
        (error) => {
          showErrorBanner(error, dispatch)
          reject(error)
        },
      )
    })
  }
}

export const submitSelfPayPayment = (
  lyraId: string,
  data: {
    paymentMethodId: string
    paymentConsent: boolean
    nonFsaHsaAttestation: boolean
    reuseCardFromUsageType?: 'INFRACTION' | 'SELF_PAY' | null
  },
) => {
  return (dispatch: AppDispatch) => {
    return new Promise(function (resolve, reject) {
      submitSelfPayPaymentData(lyraId, data).then(
        (response) => {
          dispatch({
            type: SUBMIT_HEALTHPLAN_PAYMENT,
            payload: response,
          })
          resolve(response)
        },
        (error) => {
          showErrorBanner(error, dispatch)
          reject(error)
        },
      )
    })
  }
}

export const removeHealthPlanPayment = (lyraId: string) => {
  return (dispatch: AppDispatch) => {
    return new Promise(function (resolve, reject) {
      removeHealthPlanPaymentData(lyraId).then(
        (response) => {
          dispatch(getBaseApplicationData()) // refresh data flags
          dispatch(fetchPatientData())
          dispatch({
            type: REMOVE_HEALTHPLAN_PAYMENT,
          })
          resolve(response)
        },
        (error) => {
          showErrorBanner(error, dispatch)
          reject(error)
        },
      )
    })
  }
}

export const getUnresolvedInfractions = ({ lyraId }: { lyraId: string }) => {
  return {
    action: GET_UNRESOLVED_INFRACTIONS,
    request: {
      method: 'get',
      url: `/v1/user/${lyraId}/unresolved-infractions`,
    },
  }
}

export const submitInfractionPayment = (lyraId: string, infractionCardData: PostInfractionPaymentInfoBody) => ({
  action: SUBMIT_HEALTHPLAN_PAYMENT,

  request: {
    method: 'post',
    url: `/v1/patient-payment-method/infraction/user/${lyraId}`,
    data: infractionCardData,
  },
})

export const updateInfractionPayment = (lyraId: string, infractionCardData: PostInfractionPaymentInfoBody) => ({
  action: UPDATE_INFRACTION_PAYMENT,

  request: {
    method: 'put',
    url: `/v1/patient-payment-method/infraction/user/${lyraId}`,
    data: infractionCardData,
  },
})

export const removeInfractionPayment = (lyraId: string) => ({
  action: REMOVE_INFRACTION_PAYMENT,
  request: {
    method: 'delete',
    url: `/v1/patient-payment-method/infraction/user/${lyraId}`,
  },
})

export const fetchPatientData = () => ({
  action: SET_PATIENT_DATA,
  request: {
    method: 'get',
    url: '/v1/patient/user',
  },
  errorHandler: (error: $TSFixMe) => console.error('Failed to fetch patient data', error),
})

export const setHealthPlanData = (data: $TSFixMe) => ({
  type: SET_HEALTHPLAN_DATA,
  data,
})
export const startHPAndInfractionCardsSetUp = () => ({ type: START_HEALTHPLAN_AND_INFRACTION_CARD_SETUP })
export const stopHPAndInfractionCardsSetUp = () => ({ type: STOP_HEALTHPLAN_AND_INFRACTION_CARD_SETUP })
export const updateHPAndInfractionCardRequestType = (val: string) => ({
  type: UPDATE_HEALTHPLAN_AND_INFRACTION_CARD_REQUEST_TYPE,
  val,
})

export const getPaymentHistory = (lyraId: $TSFixMe) => {
  return (dispatch: $TSFixMe) => {
    return new Promise(function (resolve, reject) {
      getPaymentHistoryData(lyraId).then(
        (response) => {
          dispatch({
            type: GET_PAYMENT_HISTORY,
            payload: response,
          })
          resolve(response)
        },
        (error) => {
          showErrorBanner(error, dispatch)
          reject(error)
        },
      )
    })
  }
}

export const getHealthPlanPayment = (lyraId: $TSFixMe, useSelfPayAPI: boolean = false) => {
  return (dispatch: $TSFixMe) => {
    return new Promise(function (resolve, reject) {
      const apiCall = useSelfPayAPI ? getHealthPlanPaymentDataV2(lyraId) : getHealthPlanPaymentData(lyraId)
      apiCall.then(
        (response) => {
          dispatch({
            type: GET_HEALTHPLAN_PAYMENT,
            payload: response,
          })
          resolve(response)
        },
        (error) => {
          showErrorBanner(error, dispatch)
          reject(error)
        },
      )
    })
  }
}

export const downloadPayments = (lyraId: $TSFixMe) => {
  return (dispatch: $TSFixMe) => {
    return new Promise(function (resolve, reject) {
      downloadPaymentsData(lyraId).then(
        (csvPayments) => {
          createCSVDownload(csvPayments.data, 'payments_history.csv')
          // @ts-expect-error TS(2794): Expected 1 arguments, but got 0. Did you forget to... Remove this comment to see the full error message
          resolve()
        },
        (error) => {
          showErrorBanner(error, dispatch)
          reject(error)
        },
      )
    })
  }
}

const showErrorBanner = (error: $TSFixMe, dispatch: $TSFixMe) => {
  dispatch(BannerActions.setBannerAndErrorMessage(error, true))
}

export const getEligibilityOptions = () => {
  return async (dispatch: $TSFixMe) => {
    try {
      const eligibilityOptions = await getEligibilityData()
      dispatch({
        type: GET_ELIGIBILITY_OPTIONS,
        data: eligibilityOptions.data,
      })
    } catch (error) {
      // Fail silently
    }
  }
}

/**
 * Upload health plan card image for OCR processing
 */
export const uploadHealthPlanCard = (lyraId: string, file: File, cardSide: CardSide) => {
  return async (dispatch: AppDispatch): Promise<HealthPlanCardUploadResponse> => {
    dispatch({
      type: UPLOAD_HEALTH_PLAN_CARD_PENDING,
      cardSide,
    })

    try {
      const response = await uploadHealthPlanCardData(lyraId, file, cardSide)
      const { status, error } = response.data

      const validation = validateUploadResponseStatus(status, error)
      if (!validation.isValid) {
        console.error(`Health plan card upload failed: ${validation.errorCode} - ${validation.errorMessage}`)
        throw new Error(`${validation.errorCode}: ${validation.errorMessage}`)
      }

      dispatch({
        type: UPLOAD_HEALTH_PLAN_CARD_FULFILLED,
        payload: response.data,
        cardSide,
      })

      return response.data
    } catch (e) {
      let errorMessage = 'Upload failed'

      if (axios.isAxiosError(e)) {
        if (e.code === 'ECONNABORTED' || e.code === 'ERR_NETWORK' || !e.response) {
          console.error(`Network error during health plan card upload: ${e.code}`)
          errorMessage = 'Network error during upload'
        } else if (e.response?.status === 413) {
          console.error(`File too large for health plan card upload`)
          errorMessage = 'File too large'
        } else {
          type ErrorResponse = { error?: { code?: string; message?: string } }
          const responseData = e.response?.data as ErrorResponse
          if (responseData?.error?.code) {
            errorMessage = responseData.error.message || 'Upload failed'
            console.error(`[Health Plan Card Upload] Backend error: ${responseData.error.code} - ${errorMessage}`)
          }
        }
      } else if (e instanceof Error) {
        errorMessage = e.message
      }

      dispatch({
        type: UPLOAD_HEALTH_PLAN_CARD_REJECTED,
        error: errorMessage,
        cardSide,
      })

      throw e
    }
  }
}

/**
 * Poll for OCR results after health plan card upload
 */
export const pollHealthPlanCardOcr = (lyraId: string, taskId: string, cardSide: CardSide) => {
  return async (dispatch: AppDispatch): Promise<OcrPollResponse> => {
    dispatch({
      type: POLL_HEALTH_PLAN_CARD_OCR_PENDING,
      cardSide,
    })

    let attempts = 0

    const poll = async (): Promise<OcrPollResponse> => {
      attempts++
      console.debug(`[Attempt ${attempts}] pollOcrResults [${cardSide}] taskID ${taskId}...`)

      try {
        const response = await pollHealthPlanCardOcrData(lyraId, taskId)
        const { status } = response.data

        console.debug(`[Attempt ${attempts}] pollOcrResults response ${status}...`)

        if (isHealthPlanCardTerminalStatus(status)) {
          if (status === OcrStatus.OCR_FAILED) {
            const errorCode = response.data.error?.code || HealthPlanCardUploadErrorCode.OCR_PROCESSING_FAILED
            const errorMessage = response.data.error?.message || 'OCR processing failed'
            console.error(`OCR failed: ${errorCode} - ${errorMessage}`)
            throw new Error(`${errorCode}: ${errorMessage}`)
          }

          dispatch({
            type: POLL_HEALTH_PLAN_CARD_OCR_FULFILLED,
            payload: response.data,
            cardSide,
          })

          return response.data
        }

        if (isHealthPlanCardProcessingStatus(status) && attempts < HEALTH_PLAN_OCR_MAX_POLL_ATTEMPTS) {
          await promiseTimeout(HEALTH_PLAN_OCR_POLL_INTERVAL_MS)
          return await poll()
        }

        if (attempts >= HEALTH_PLAN_OCR_MAX_POLL_ATTEMPTS) {
          const timeoutError = `OCR processing timed out after ${
            (HEALTH_PLAN_OCR_MAX_POLL_ATTEMPTS * HEALTH_PLAN_OCR_POLL_INTERVAL_MS) / 1000
          } seconds`
          console.error(`${timeoutError}`)
          throw new Error(timeoutError)
        }

        throw new Error(`Unexpected OCR status: ${status}`)
      } catch (error) {
        if (error instanceof Error && (error.message.includes('timed out') || error.message.includes('Unexpected'))) {
          throw error
        }

        if (axios.isAxiosError(error)) {
          if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || !error.response) {
            console.error(`Network error during polling: ${error.code}`)
          }
        }

        throw new Error(
          `Failed to poll OCR results for taskId ${taskId}: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
        )
      }
    }

    try {
      return await poll()
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'OCR processing failed'
      dispatch({
        type: POLL_HEALTH_PLAN_CARD_OCR_REJECTED,
        error: errorMessage,
        cardSide,
      })
      throw e
    }
  }
}

/**
 * Reset card upload state
 */
export const resetCardUploadState = () => ({
  type: RESET_CARD_UPLOAD_STATE,
})
